import Layer from "../../Layer";
import PageHeader from "../../Component/PageHeader";
import CardReportSettings from "../../Component/Card/Report/CardReportSettings";
import CardUserProductionDetailResults from "../../Component/Card/Report/CardUserProductionDetailResults";
import Setting from "../../Utility/Setting";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import api from "../../Seating/Security/api";

export default class UserProductionDetail extends Layer {
  /**
   * Call method to set grouping and column widths in state.
   */
  afterComponentDidMount() {
    this.setState(
      {
        grouping: Setting.get(`reports.${this.getDetailKey()}.grouping`) || [],
        sorting: Setting.get(`reports.${this.getDetailKey()}.sorting`) || [],
      },
      () => {
        this.setColumnWidths();
      },
    );
  }

  /**
   * Returns a camel-cased string to use as a unique identifier
   * for the subclass, based on its `getTitle()` implementation.
   */
  getDetailKey() {
    const header = this.getTitle();
    if (!header) return "userProductionDetail";

    const noSpaces = header
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");

    const lowercaseFirstLetter =
      noSpaces.charAt(0).toLowerCase() + noSpaces.slice(1);

    return `userProductionDetail.${lowercaseFirstLetter}`;
  }

  /**
   * Sets the column widths after component has mounted.
   */
  setColumnWidths() {
    let defaultColumnWidths = [];

    this.columns.forEach((column) => {
      defaultColumnWidths.push({
        columnName: column.name,
        width: column.width,
      });
    });

    this.setState({
      defaultColumnWidths: defaultColumnWidths,
    });
  }

  /**
   * Sets new grouping in state and local storage.
   */
  handleGroupingChange(grouping) {
    this.setState({
      grouping: grouping,
    });

    Setting.set(`reports.${this.getDetailKey()}.grouping`, grouping);
  }

  /**
   * Sets new sorting in state and local storage.
   */
  handleSortingChange(sorting) {
    this.setState({
      sorting: sorting,
    });

    Setting.set(`reports.${this.getDetailKey()}.sorting`, sorting);
  }

  /**
   * Fetches and processes User Production Details, updating the component's
   * state with the results.
   *
   * @param {Object} settings - Configuration settings for the User PRoduction Details
   * request.
   */
  async getUserProductionDetails(settings) {
    let transformedSettings = {
      beginDate: settings.dateRange.beginDate,
      endDate: settings.dateRange.endDate,
      userProductionDetailTypeTermId: this.getUserProductionDetailTypeTermId(),
    };

    this.setState({ loading: true });

    return api
      .send("POST", "/userProductionDetail/search", transformedSettings)
      .then((response) => {
        this.handleTransformRows(response);
      })
      .catch((error) => {
        console.error(error);
        this.setState({ loading: false });
        NotistackEnqueueSnackbar("Error retrieving user production results", {
          variant: "error",
        });
      });
  }

  /**
   * Processes the response data from the `getUserProductionDetails` API call.
   *
   * In the base class, this function does not perform any transformation and simply returns
   * the raw response.
   * This method is intended to be overridden by subclasses
   *
   * @param {Object} response - The raw response object returned from the API.
   * @returns {Object} The original response, unmodified.
   */
  handleTransformRows(response) {
    return response;
  }

  /**
   * Render the content of the page.
   */
  renderContent() {
    return (
      <>
        <CardReportSettings
          onRun={(settings) => this.getUserProductionDetails(settings)}
          isLoading={this.state.loading}
          settingComponents={{
            dateRange: {
              validationRules: {
                beginDate: {
                  required: true,
                },
                endDate: {
                  required: true,
                },
              },
            },
          }}
        />
        <CardUserProductionDetailResults
          rows={this.state.rows || []}
          columns={this.columns}
          isLoading={this.state.loading}
          onClick={(row) => console.log(row)}
          defaultColumnWidths={this.state.defaultColumnWidths}
          groupSummaryItems={this.getGroupSummaryItems()}
          grouping={this.state.grouping}
          onGroupingChange={this.handleGroupingChange.bind(this)}
          sorting={this.state.sorting}
          onSortingChange={this.handleSortingChange.bind(this)}
          title={this.getTitle()}
          columnNames={
            this.getCSVExportColumnNames()[this.getTitle().toLowerCase()]
          }
        />
      </>
    );
  }

  /**
   * Render the page header a title and optional buttons.
   */
  renderHeader() {
    return <PageHeader title={this.getTitle()} />;
  }

  /**
   * @returns {string} The title of the page. Defaults to empty; subclasses
   * should override.
   */
  getTitle() {
    return null;
  }

  /**
   * @returns The max width of this page.
   */
  getMaxWidth() {
    return 1400;
  }
}
